import React, { useState, useRef, useEffect } from 'react';
import {
  Plus,
  Search,
  Trash2,
  PanelLeftClose,
  PanelLeftOpen,
  MessageSquare,
} from 'lucide-react';
import type { ConversationSummary } from '../../services/api';
import type { ChatContractOption } from './ContractChat';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChatHistorySidebarProps {
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onNewChat: () => void;
  onDeleteConversation: (id: string) => void;
  onSearch: (query: string) => void;
  searchQuery: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  contracts: ChatContractOption[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeDate(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 7) return 'Previous 7 days';
  return 'Older';
}

function groupByDate(conversations: ConversationSummary[]): Map<string, ConversationSummary[]> {
  const groups = new Map<string, ConversationSummary[]>();
  for (const conv of conversations) {
    const group = relativeDate(conv.updatedAt);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(conv);
  }
  return groups;
}

function resolveContractNames(
  contractIds: string[],
  contracts: ChatContractOption[],
): string {
  if (contractIds.length === 0) return '';
  const names = contractIds
    .map((id) => contracts.find((c) => c.id === id)?.name)
    .filter(Boolean);
  return names.join(', ');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ChatHistorySidebar: React.FC<ChatHistorySidebarProps> = ({
  conversations,
  activeConversationId,
  onSelectConversation,
  onNewChat,
  onDeleteConversation,
  onSearch,
  searchQuery,
  isCollapsed,
  onToggleCollapse,
  contracts,
}) => {
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Clear delete confirm when clicking elsewhere
  useEffect(() => {
    if (!deleteConfirmId) return;
    const timer = setTimeout(() => setDeleteConfirmId(null), 3000);
    return () => clearTimeout(timer);
  }, [deleteConfirmId]);

  const handleSearchChange = (value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearch(value);
    }, 300);
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (deleteConfirmId === id) {
      onDeleteConversation(id);
      setDeleteConfirmId(null);
    } else {
      setDeleteConfirmId(id);
    }
  };

  // Collapsed state: show toggle button only
  if (isCollapsed) {
    return (
      <div className="flex flex-col items-center py-3 px-1 border-r border-secondary-200 bg-white">
        <button
          onClick={onToggleCollapse}
          className="p-2 rounded-md text-secondary-500 hover:bg-secondary-100 hover:text-secondary-700 transition-colors"
          title="Expand sidebar"
        >
          <PanelLeftOpen className="h-5 w-5" />
        </button>
        <button
          onClick={onNewChat}
          className="mt-2 p-2 rounded-md text-primary-600 hover:bg-primary-50 transition-colors"
          title="New chat"
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>
    );
  }

  const grouped = groupByDate(conversations);
  const showGroups = !searchQuery;

  return (
    <div className="flex flex-col w-72 border-r border-secondary-200 bg-white h-full" data-testid="chat-sidebar">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-secondary-200">
        <button
          onClick={onNewChat}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          data-testid="new-chat-button"
        >
          <Plus className="h-4 w-4" />
          New Chat
        </button>
        <button
          onClick={onToggleCollapse}
          className="p-1.5 rounded-md text-secondary-400 hover:bg-secondary-100 hover:text-secondary-600 transition-colors"
          title="Collapse sidebar"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-secondary-100">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-secondary-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search conversations..."
            defaultValue={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-secondary-200 bg-secondary-50 text-secondary-700 placeholder-secondary-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 focus:bg-white"
            data-testid="chat-search"
          />
        </div>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-secondary-400 px-4">
            <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-xs text-center">
              {searchQuery ? 'No conversations match your search' : 'No conversations yet'}
            </p>
          </div>
        ) : showGroups ? (
          Array.from(grouped.entries()).map(([group, convs]) => (
            <div key={group}>
              <div className="px-3 pt-3 pb-1">
                <span className="text-xs font-semibold text-secondary-400 uppercase tracking-wide">
                  {group}
                </span>
              </div>
              {convs.map((conv) => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  isActive={conv.id === activeConversationId}
                  contracts={contracts}
                  deleteConfirmId={deleteConfirmId}
                  onSelect={onSelectConversation}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          ))
        ) : (
          conversations.map((conv) => (
            <ConversationItem
              key={conv.id}
              conversation={conv}
              isActive={conv.id === activeConversationId}
              contracts={contracts}
              deleteConfirmId={deleteConfirmId}
              onSelect={onSelectConversation}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Conversation list item
// ---------------------------------------------------------------------------

const ConversationItem: React.FC<{
  conversation: ConversationSummary;
  isActive: boolean;
  contracts: ChatContractOption[];
  deleteConfirmId: string | null;
  onSelect: (id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
}> = ({ conversation, isActive, contracts, deleteConfirmId, onSelect, onDelete }) => {
  const contractNames = resolveContractNames(conversation.contractIds, contracts);
  const time = new Date(conversation.updatedAt);
  const timeStr = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <button
      onClick={() => onSelect(conversation.id)}
      className={`group w-full text-left px-3 py-2.5 transition-colors ${
        isActive
          ? 'bg-primary-50 border-r-2 border-primary-500'
          : 'hover:bg-secondary-50'
      }`}
      data-testid={`conversation-${conversation.id}`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm truncate ${
              isActive ? 'font-semibold text-primary-800' : 'font-medium text-secondary-800'
            }`}
          >
            {conversation.title}
          </p>
          {contractNames && (
            <p className="text-xs text-secondary-400 truncate mt-0.5">{contractNames}</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-xs text-secondary-400">{timeStr}</span>
          <button
            onClick={(e) => onDelete(e, conversation.id)}
            className={`p-1 rounded transition-colors ${
              deleteConfirmId === conversation.id
                ? 'text-red-600 bg-red-50'
                : 'text-secondary-300 opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50'
            }`}
            title={deleteConfirmId === conversation.id ? 'Click again to confirm' : 'Delete conversation'}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </button>
  );
};

export default ChatHistorySidebar;
